import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  Check,
  Copy,
  DollarSign,
  Edit2,
  Eye,
  EyeOff,
  KeyRound,
  Lock,
  Percent,
  Plus,
  RefreshCw,
  Search,
  Shield,
  ToggleLeft,
  ToggleRight,
  User as UserIcon,
  X
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_TEMPORARY_PASSWORD = 'Qwerty123*';

const DEFAULT_INSURER_PERCENTAGES = {
  ASSA: 100,
  'Óptima de Seguros': 100,
  'La Regional de Seguros': 100,
  'Seguros Fedpa': 100,
  'Seguros Global': 60
};

const DEFAULT_INSURER_KM_PRICES = {
  ASSA: '',
  'Óptima de Seguros': '',
  'La Regional de Seguros': '',
  'Seguros Fedpa': '',
  'Seguros Global': ''
};

const DEFAULT_PERMISSIONS = ['dashboard', 'invoices', 'tasks'];
const SOURCE_MAPPING_FIELDS = [
  'ASSA',
  'Óptima de Seguros',
  'La Regional de Seguros',
  'Seguros Fedpa',
  'Seguros Global'
];

const normalizeInsurerLabel = (value = '') => {
  const insurer = String(value || '').trim();
  const normalized = insurer
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  if (normalized.includes('ptima de seguros')) return 'Óptima de Seguros';
  if (normalized.includes('la regional de seguros')) return 'La Regional de Seguros';
  if (normalized.includes('seguros fedpa')) return 'Seguros Fedpa';
  if (normalized.includes('seguros global') || normalized.includes('global')) return 'Seguros Global';
  if (normalized.includes('assa')) return 'ASSA';

  return insurer;
};

const normalizeInsurerPercentages = (percentages = {}) => {
  const normalizedPercentages = {};

  Object.entries(percentages || {}).forEach(([insurer, percentage]) => {
    const normalizedInsurer = normalizeInsurerLabel(insurer);
    if (normalizedInsurer) {
      normalizedPercentages[normalizedInsurer] = percentage;
    }
  });

  return {
    ...DEFAULT_INSURER_PERCENTAGES,
    ...normalizedPercentages
  };
};

const normalizeInsurerKmPrices = (prices = {}) => {
  const normalizedPrices = {};

  Object.entries(prices || {}).forEach(([insurer, price]) => {
    const normalizedInsurer = normalizeInsurerLabel(insurer);
    if (normalizedInsurer) normalizedPrices[normalizedInsurer] = price ?? '';
  });

  return { ...DEFAULT_INSURER_KM_PRICES, ...normalizedPrices };
};

const serializeInsurerKmPrices = (prices = {}) =>
  Object.fromEntries(
    Object.entries(normalizeInsurerKmPrices(prices)).map(([insurer, price]) => [
      insurer,
      price === '' || price === null ? null : Number(price)
    ])
  );

const generateReadablePassword = () => DEFAULT_TEMPORARY_PASSWORD;

const availableSections = [
  { id: 'dashboard', label: 'Dashboard', icon: 'DB' },
  { id: 'invoices', label: 'Facturación', icon: '$' },
  { id: 'schedules', label: 'Horarios', icon: 'HR' },
  { id: 'loans', label: 'Préstamos', icon: '$$' },
  { id: 'tasks', label: 'Solicitudes', icon: 'OK' },
  { id: 'team', label: 'Usuarios', icon: 'US', adminOnly: true }
];

const getInitialFormData = () => ({
  name: '',
  email: '',
  password: generateReadablePassword(),
  sourceMappings: SOURCE_MAPPING_FIELDS.reduce((acc, source) => {
    acc[source] = '';
    return acc;
  }, {}),
  role: 'collaborator',
  insurerPercentages: { ...DEFAULT_INSURER_PERCENTAGES },
  insurerKmPrices: { ...DEFAULT_INSURER_KM_PRICES },
  permissions: [...DEFAULT_PERMISSIONS]
});

const ActionConfirmModal = ({
  open,
  title,
  message,
  confirmLabel,
  confirmClassName = 'bg-primary hover:bg-primary-hover',
  onCancel,
  onConfirm
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <div className="border-b border-slate-100 px-6 py-5">
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm leading-6 text-slate-600">{message}</p>
        </div>
        <div className="flex justify-end gap-3 border-t border-slate-100 px-6 py-4">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`rounded-xl px-4 py-2 text-sm font-medium text-white transition-colors ${confirmClassName}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const Team = () => {
  const { currentUser, isAdmin, isManager, changePassword } = useAuth();
  const { addNotification } = useNotification();
  const canManageUsers = isManager;
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState(getInitialFormData());
  const [credentialModal, setCredentialModal] = useState({
    open: false,
    userName: '',
    password: ''
  });
  const [copiedValue, setCopiedValue] = useState('');
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    next: false,
    confirm: false
  });
  const [passwordError, setPasswordError] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [confirmState, setConfirmState] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: '',
    confirmClassName: 'bg-primary hover:bg-primary-hover',
    action: null
  });

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users`);
      setUsers((res.data || []).map((user) => ({
        ...user,
        insurerPercentages: normalizeInsurerPercentages(user.insurerPercentages),
        insurerKmPrices: normalizeInsurerKmPrices(user.insurerKmPrices)
      })));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = users.filter((user) =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeUsers = filteredUsers.filter((user) => user.active !== false);
  const inactiveUsers = filteredUsers.filter((user) => user.active === false);

  const resetForm = () => {
    setFormData(getInitialFormData());
    setIsEditMode(false);
    setSelectedUser(null);
  };

  const normalizeSourceMappings = (mappings = {}) =>
    Object.fromEntries(
      SOURCE_MAPPING_FIELDS.map((source) => [
        source,
        (mappings[source] || '')
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean)
      ]).filter(([, values]) => values.length > 0)
    );

  const copyToClipboard = async (value, successMessage = 'Texto copiado correctamente') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedValue(value);
      addNotification('Copiado', successMessage, 'success');
      window.setTimeout(() => setCopiedValue(''), 2000);
    } catch (error) {
      console.error('Error copying text:', error);
      addNotification('Error', 'No fue posible copiar el texto', 'error');
    }
  };

  const regenerateTemporaryPassword = () => {
    setFormData((prev) => ({
      ...prev,
      password: generateReadablePassword()
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || (!isEditMode && !formData.password)) {
      addNotification('Campos requeridos', 'Por favor completa todos los campos requeridos.', 'warning');
      return;
    }

    try {
      if (isEditMode && selectedUser) {
        const payload = {
          name: formData.name,
          email: formData.email,
          sourceMappings: normalizeSourceMappings(formData.sourceMappings),
          role: formData.role,
          insurerPercentages: normalizeInsurerPercentages(formData.insurerPercentages),
          insurerKmPrices: serializeInsurerKmPrices(formData.insurerKmPrices),
          permissions: formData.permissions,
          avatar: formData.name.charAt(0).toUpperCase(),
          mustChangePassword: selectedUser.mustChangePassword || false
        };

        if (formData.password) {
          payload.password = formData.password;
        }

        await axios.put(`${API_URL}/api/users/${selectedUser.id}`, payload);
        addNotification('Usuario Actualizado', `Se actualizó ${formData.name}`, 'success');
      } else {
        await axios.post(`${API_URL}/api/users`, {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          sourceMappings: normalizeSourceMappings(formData.sourceMappings),
          role: formData.role,
          insurerPercentages: normalizeInsurerPercentages(formData.insurerPercentages),
          insurerKmPrices: serializeInsurerKmPrices(formData.insurerKmPrices),
          permissions: formData.permissions,
          avatar: formData.name.charAt(0).toUpperCase(),
          mustChangePassword: true
        });
        setCredentialModal({
          open: true,
          userName: formData.name,
          password: formData.password
        });
        addNotification('Usuario Creado', `Se creó ${formData.name}`, 'success');
      }

      fetchUsers();
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving user:', error);
      addNotification('Error', error.response?.data?.detail || 'Error al guardar usuario', 'error');
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      sourceMappings: SOURCE_MAPPING_FIELDS.reduce((acc, source) => {
        acc[source] = (user.sourceMappings?.[source] || []).join(', ');
        return acc;
      }, {}),
      role: user.role,
      insurerPercentages: normalizeInsurerPercentages(user.insurerPercentages),
      insurerKmPrices: normalizeInsurerKmPrices(user.insurerKmPrices),
      permissions: user.permissions || [...DEFAULT_PERMISSIONS]
    });
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const closeConfirmModal = () => {
    setConfirmState((prev) => ({
      ...prev,
      open: false,
      action: null
    }));
  };

  const runConfirmAction = async () => {
    if (!confirmState.action) return;

    try {
      await confirmState.action();
    } finally {
      closeConfirmModal();
    }
  };

  const handleToggleActive = async (user) => {
    const activating = user.active === false;
    setConfirmState({
      open: true,
      title: activating ? 'Activar usuario' : 'Desactivar usuario',
      message: activating
        ? `¿Deseas habilitar nuevamente el acceso de "${user.name}"?`
        : `¿Deseas desactivar a "${user.name}"? No podrá iniciar sesión hasta que lo actives nuevamente.`,
      confirmLabel: activating ? 'Activar' : 'Desactivar',
      confirmClassName: activating ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700',
      action: async () => {
        try {
          const response = await axios.patch(`${API_URL}/api/users/${user.id}/status`, {
            active: activating
          });
          const savedUser = {
            ...response.data,
            insurerPercentages: normalizeInsurerPercentages(response.data.insurerPercentages),
            insurerKmPrices: normalizeInsurerKmPrices(response.data.insurerKmPrices)
          };
          setUsers((currentUsers) => currentUsers.map((item) => (
            item.id === savedUser.id ? savedUser : item
          )));
          addNotification(
            activating ? 'Usuario activado' : 'Usuario desactivado',
            `${user.name} fue ${activating ? 'activado' : 'desactivado'} correctamente`,
            'success'
          );
        } catch (error) {
          console.error('Error toggling user status:', error);
          addNotification('Error', error.response?.data?.detail || 'No se pudo cambiar el estado del usuario', 'error');
          throw error;
        }
      }
    });
  };

  const handleResetPassword = async (user) => {
    setConfirmState({
      open: true,
      title: 'Restablecer contraseña',
      message: `Se generará una nueva contraseña temporal para "${user.name}" y se forzará el cambio en su próximo ingreso.`,
      confirmLabel: 'Restablecer',
      confirmClassName: 'bg-amber-500 hover:bg-amber-600',
      action: async () => {
        try {
          const res = await axios.post(`${API_URL}/api/users/${user.id}/reset-password`);
          setCredentialModal({
            open: true,
            userName: user.name,
            password: res.data.temporaryPassword
          });
          fetchUsers();
          addNotification('Contraseña restablecida', `Se generó una nueva contraseña temporal para ${user.name}`, 'success');
        } catch (error) {
          console.error('Error resetting password:', error);
          addNotification('Error', error.response?.data?.detail || 'No fue posible restablecer la contraseña', 'error');
        }
      }
    });
  };

  const handlePercentageChange = (insurer, value) => {
    setFormData((prev) => ({
      ...prev,
      insurerPercentages: {
        ...prev.insurerPercentages,
        [insurer]: parseFloat(value) || 0
      }
    }));
  };

  const handleKmPriceChange = (insurer, value) => {
    setFormData((prev) => ({
      ...prev,
      insurerKmPrices: {
        ...prev.insurerKmPrices,
        [insurer]: value
      }
    }));
  };

  const handlePermissionToggle = (sectionId) => {
    setFormData((prev) => {
      const currentPermissions = prev.permissions || [];
      const hasPermission = currentPermissions.includes(sectionId);

      return {
        ...prev,
        permissions: hasPermission
          ? currentPermissions.filter((permission) => permission !== sectionId)
          : [...currentPermissions, sectionId]
      };
    });
  };

  const handleRoleChange = (newRole) => {
    let defaultPermissions = [...DEFAULT_PERMISSIONS];

    if (newRole === 'admin') {
      defaultPermissions = ['dashboard', 'invoices', 'schedules', 'loans', 'tasks', 'team'];
    }

    setFormData((prev) => ({
      ...prev,
      role: newRole,
      permissions: defaultPermissions
    }));
  };

  const openPasswordModal = () => {
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswords({
      current: false,
      next: false,
      confirm: false
    });
    setPasswordError('');
    setIsSavingPassword(false);
    setIsPasswordModalOpen(true);
  };

  const closePasswordModal = () => {
    setIsPasswordModalOpen(false);
    setPasswordError('');
    setIsSavingPassword(false);
  };

  const handlePasswordField = (field, value) => {
    setPasswordForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleAdminPasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('Completa todos los campos.');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('La confirmación no coincide con la nueva contraseña.');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordError('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }

    setIsSavingPassword(true);

    try {
      await changePassword({
        email: currentUser?.email,
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      addNotification('Contraseña actualizada', 'La contraseña del administrador se actualizó correctamente.', 'success');
      closePasswordModal();
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'No fue posible actualizar la contraseña.');
      setIsSavingPassword(false);
    }
  };

  const renderPasswordField = ({ id, label, field, visibleKey, placeholder }) => (
    <div>
      <label htmlFor={id} className="mb-2 block text-sm font-medium text-slate-700">
        {label}
      </label>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
        <input
          id={id}
          type={showPasswords[visibleKey] ? 'text' : 'password'}
          value={passwordForm[field]}
          onChange={(e) => handlePasswordField(field, e.target.value)}
          className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-12 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={() => togglePasswordVisibility(visibleKey)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-700"
          aria-label={showPasswords[visibleKey] ? `Ocultar ${label.toLowerCase()}` : `Mostrar ${label.toLowerCase()}`}
        >
          {showPasswords[visibleKey] ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );

  return (
    <div data-testid="team-page" className="flex h-full min-h-0 flex-col gap-6 overflow-hidden">
      <div className="flex flex-shrink-0 flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
                  <h1 className="text-2xl font-heading font-bold text-slate-800">Gestión de Usuarios</h1>
          <p className="mt-1 text-sm text-slate-500">Administra los accesos y porcentajes de pago</p>
        </div>
        {isAdmin && (
          <button
            data-testid="add-user-btn"
            onClick={() => {
              resetForm();
              setIsModalOpen(true);
            }}
            className="flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 font-medium text-white shadow-lg shadow-primary/25 transition-all hover:bg-primary-hover"
          >
            <Plus size={18} />
            Nuevo Usuario
          </button>
        )}
      </div>

      <div className="relative flex-shrink-0">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
        <input
          type="text"
          placeholder="Buscar por nombre o email..."
          className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-12 pr-4 transition-all focus:outline-none focus:ring-2 focus:ring-primary"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1 space-y-6">
        <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div>
            <p className="text-sm font-semibold text-slate-800">Ver usuarios desactivados</p>
            <p className="text-xs text-slate-500">Se muestran separados de los activos</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={showInactiveUsers}
            onClick={() => setShowInactiveUsers((prev) => !prev)}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
              showInactiveUsers ? 'bg-primary' : 'bg-slate-300'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                showInactiveUsers ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-800">Usuarios activos</h3>
              <p className="text-xs text-slate-500">{activeUsers.length} registrados</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-[800px] w-full text-left">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-4 py-4 text-sm font-semibold text-slate-600">Usuario</th>
                  <th className="px-4 py-4 text-sm font-semibold text-slate-600">Email</th>
                  <th className="px-4 py-4 text-sm font-semibold text-slate-600">Rol</th>
                  <th className="px-4 py-4 text-sm font-semibold text-slate-600">Porcentajes</th>
                  {canManageUsers && <th className="px-4 py-4 text-center text-sm font-semibold text-slate-600">Acciones</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {activeUsers.map((user, index) => (
                  <tr key={`${user.id || 'user'}-${user.email}-${index}`} className="transition-colors hover:bg-slate-50/50">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent font-bold text-white shadow-sm">
                          {user.avatar || user.name.charAt(0)}
                        </div>
                        <span className="font-medium text-slate-800">{user.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600">{user.email}</td>
                    <td className="px-4 py-4">
                      {user.role === 'admin' ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                          <Shield size={12} />
                          Admin
                        </span>
                      ) : user.role === 'office' ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-700">
                          <UserIcon size={12} />
                          Oficina
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                          <UserIcon size={12} />
                          Colaborador
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {user.role === 'collaborator' && user.insurerPercentages ? (
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(user.insurerPercentages).map(([insurer, percentage]) => (
                            <span key={insurer} className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-600">
                              {insurer.split(' ')[0]}: {percentage}%
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">N/A</span>
                      )}
                    </td>
                    {canManageUsers && (
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleEdit(user)}
                            className="rounded-xl p-2 text-blue-600 transition-colors hover:bg-blue-50"
                            title="Editar"
                          >
                            <Edit2 size={18} />
                          </button>
                          {isAdmin && user.id === currentUser?.id && user.role === 'admin' && (
                            <button
                              onClick={openPasswordModal}
                              className="rounded-xl p-2 text-primary transition-colors hover:bg-primary/10"
                              title="Cambiar contraseña"
                            >
                              <KeyRound size={18} />
                            </button>
                          )}
                          {isAdmin && user.id !== currentUser?.id && (
                            <button
                              onClick={() => handleResetPassword(user)}
                              className="rounded-xl p-2 text-amber-600 transition-colors hover:bg-amber-50"
                              title="Restablecer contraseña"
                            >
                              <KeyRound size={18} />
                            </button>
                          )}
                          {isAdmin && user.id !== currentUser?.id && (
                            <button
                              onClick={() => handleToggleActive(user)}
                              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50"
                              title="Desactivar usuario"
                            >
                              <ToggleLeft size={18} /> Desactivar
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {activeUsers.length === 0 && (
            <div className="p-12 text-center">
              <UserIcon size={48} className="mx-auto mb-4 text-slate-200" />
              <p className="mb-1 text-lg font-medium text-slate-600">No hay usuarios activos</p>
              <p className="text-sm text-slate-500">Crea o activa un usuario para comenzar</p>
            </div>
          )}
        </div>

        {showInactiveUsers && (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-800">Usuarios desactivados</h3>
                <p className="text-xs text-slate-500">{inactiveUsers.length} registrados</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-[800px] w-full text-left">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th className="px-4 py-4 text-sm font-semibold text-slate-600">Usuario</th>
                    <th className="px-4 py-4 text-sm font-semibold text-slate-600">Email</th>
                    <th className="px-4 py-4 text-sm font-semibold text-slate-600">Rol</th>
                    <th className="px-4 py-4 text-sm font-semibold text-slate-600">Porcentajes</th>
                    {canManageUsers && <th className="px-4 py-4 text-center text-sm font-semibold text-slate-600">Acciones</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {inactiveUsers.map((user, index) => (
                    <tr key={`${user.id || 'user'}-${user.email}-${index}`} className="transition-colors hover:bg-slate-50/50 bg-slate-50/40">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-slate-400 to-slate-600 font-bold text-white shadow-sm">
                            {user.avatar || user.name.charAt(0)}
                          </div>
                          <div>
                            <span className="font-medium text-slate-800">{user.name}</span>
                            <div className="text-xs text-slate-400">Desactivado</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-600">{user.email}</td>
                      <td className="px-4 py-4">
                        {user.role === 'admin' ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                            <Shield size={12} />
                            Admin
                          </span>
                        ) : user.role === 'office' ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-700">
                            <UserIcon size={12} />
                            Oficina
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                            <UserIcon size={12} />
                            Colaborador
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {user.role === 'collaborator' && user.insurerPercentages ? (
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(user.insurerPercentages).map(([insurer, percentage]) => (
                              <span key={insurer} className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-600">
                                {insurer.split(' ')[0]}: {percentage}%
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">N/A</span>
                        )}
                      </td>
                      {canManageUsers && (
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleEdit(user)}
                              className="rounded-xl p-2 text-blue-600 transition-colors hover:bg-blue-50"
                              title="Editar"
                            >
                              <Edit2 size={18} />
                            </button>
                          {isAdmin && user.id !== currentUser?.id && (
                            <button
                              onClick={() => handleToggleActive(user)}
                              className="rounded-xl p-2 text-emerald-700 transition-colors hover:bg-emerald-50"
                              title="Activar usuario"
                            >
                              <ToggleRight size={18} />
                            </button>
                          )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {inactiveUsers.length === 0 && (
              <div className="p-12 text-center">
                <UserIcon size={48} className="mx-auto mb-4 text-slate-200" />
                <p className="mb-1 text-lg font-medium text-slate-600">No hay usuarios desactivados</p>
                <p className="text-sm text-slate-500">Cuando desactives un usuario aparecerá aquí</p>
              </div>
            )}
          </div>
        )}
      </div>

      {!showInactiveUsers && inactiveUsers.length > 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-500">
          Hay {inactiveUsers.length} usuario{inactiveUsers.length === 1 ? '' : 's'} desactivado{inactiveUsers.length === 1 ? '' : 's'}. Activa el switch para verlos separados.
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto overflow-hidden rounded-2xl bg-white shadow-2xl animate-scale-in">
            <div className="sticky top-0 flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white p-6">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-primary/10 p-2 text-primary">
                  <UserIcon size={20} />
                </div>
                <h3 className="font-heading font-bold text-slate-800">
                  {isEditMode ? 'Editar Usuario' : 'Nuevo Usuario'}
                </h3>
              </div>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  resetForm();
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 p-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Nombre *</label>
                  <input
                    type="text"
                    required
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Ej: Juan Perez"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Email *</label>
                  <input
                    type="email"
                    required
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="ejemplo@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                       Contraseña temporal {!isEditMode && '*'}
                </label>
                {isEditMode ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                        La contraseña no se edita aquí. Usa la acción <span className="font-semibold">Restablecer contraseña</span> desde la tabla para generar una nueva clave temporal y obligar el cambio en el próximo acceso.
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        required
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 font-medium text-slate-700 focus:outline-none"
                        value={formData.password}
                      />
                      <button
                        type="button"
                                               onClick={() => copyToClipboard(formData.password, 'Contraseña temporal copiada')}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100"
                      >
                        {copiedValue === formData.password ? <Check size={16} /> : <Copy size={16} />}
                        Copiar
                      </button>
                      <button
                        type="button"
                        onClick={regenerateTemporaryPassword}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100"
                      >
                        <RefreshCw size={16} />
                        Regenerar
                      </button>
                    </div>
                    <p className="text-xs text-slate-500">
                      Esta contraseña se entrega al usuario y quedará obligatoriamente reemplazada en su primer ingreso.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Rol</label>
                <select
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.role}
                  onChange={(e) => handleRoleChange(e.target.value)}
                >
                  <option value="collaborator">Colaborador (Técnico)</option>
                  <option value="office">Oficina (Asistente)</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>

              {formData.role !== 'admin' && (
                <div className="border-t border-slate-200 pt-6">
                  <div className="mb-4 flex items-center gap-2">
                    <Shield size={20} className="text-primary" />
                    <h4 className="font-semibold text-slate-800">Permisos de Acceso</h4>
                  </div>
                  <p className="mb-4 text-sm text-slate-600">
                    Selecciona a que secciones puede acceder este usuario
                  </p>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {availableSections.map((section) => {
                      const isChecked = (formData.permissions || []).includes(section.id);
                      return (
                        <div key={section.id} className="flex items-center">
                          <input
                            type="checkbox"
                            id={`permission-${section.id}`}
                            checked={isChecked}
                            onChange={() => handlePermissionToggle(section.id)}
                            className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                          />
                          <label
                            htmlFor={`permission-${section.id}`}
                            className="ml-2 flex items-center gap-2 text-sm text-slate-700"
                          >
                            <span>{section.icon}</span>
                            <span>{section.label}</span>
                            {section.adminOnly && (
                              <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                                Avanzado
                              </span>
                            )}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {formData.role === 'admin' && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                  <p className="text-sm text-blue-800">
                    Los administradores tienen acceso completo a todas las secciones del sistema
                  </p>
                </div>
              )}

              {formData.role === 'collaborator' && (
                <div className="space-y-8 border-t border-slate-200 pt-6">
                  <div>
                  <div className="mb-4 flex items-center gap-2">
                    <Search size={20} className="text-primary" />
                    <h4 className="font-semibold text-slate-800">Mapeo por fuente</h4>
                  </div>
                  <p className="mb-4 text-sm text-slate-600">
                    Define como aparece este técnico en cada fuente externa para amarrarlo siempre a su id interno.
                  </p>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {SOURCE_MAPPING_FIELDS.map((source) => (
                      <div key={source} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <label className="mb-2 block text-sm font-medium text-slate-700">{source}</label>
                        <textarea
                          className="min-h-[88px] w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Ej: Juan Perez, Perez Grúa"
                          value={formData.sourceMappings[source] || ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              sourceMappings: {
                                ...prev.sourceMappings,
                                [source]: e.target.value
                              }
                            }))
                          }
                        />
                        <p className="mt-2 text-xs text-slate-500">Separa variantes con coma.</p>
                      </div>
                    ))}
                  </div>
                  </div>

                  <div className="border-t border-slate-200 pt-6">
                    <div className="mb-4 flex items-center gap-2">
                      <DollarSign size={20} className="text-primary" />
                      <h4 className="font-semibold text-slate-800">Precio por km por Aseguradora</h4>
                    </div>
                    <p className="mb-4 text-sm text-slate-600">
                      Define la tarifa que se aplicará automáticamente a las objeciones por kilometraje.
                    </p>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {Object.entries(formData.insurerKmPrices).map(([insurer, price]) => (
                        <div key={insurer} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <label className="mb-2 block text-sm font-medium text-slate-700">{insurer}</label>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-600">$</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="flex-1 rounded-lg border border-slate-200 bg-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                              value={price}
                              onChange={(e) => handleKmPriceChange(insurer, e.target.value)}
                              placeholder="Sin definir"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {formData.role === 'collaborator' && (
                <div className="border-t border-slate-200 pt-6">
                  <div className="mb-4 flex items-center gap-2">
                    <Percent size={20} className="text-primary" />
                    <h4 className="font-semibold text-slate-800">Porcentajes de Pago por Aseguradora</h4>
                  </div>
                  <p className="mb-4 text-sm text-slate-600">
                    Define que porcentaje del monto total recibirá el técnico por cada aseguradora.
                    <br />
                    <span className="text-xs text-slate-500">
                      Ejemplo: si el servicio cuesta $12 y el porcentaje es 75% o 66.67%, el técnico
                      recibe ese porcentaje del monto.
                    </span>
                  </p>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {Object.entries(formData.insurerPercentages).map(([insurer, percentage]) => (
                      <div key={insurer} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <label className="mb-2 block text-sm font-medium text-slate-700">{insurer}</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            className="flex-1 rounded-lg border border-slate-200 bg-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                            value={percentage}
                            onChange={(e) => handlePercentageChange(insurer, e.target.value)}
                            placeholder="Ej: 66.67"
                          />
                          <span className="font-medium text-slate-600">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                  className="rounded-xl px-6 py-2.5 font-medium text-slate-600 transition-colors hover:bg-slate-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="rounded-xl bg-primary px-8 py-2.5 font-bold text-white shadow-lg shadow-primary/25 transition-all hover:bg-primary-hover"
                >
                  {isEditMode ? 'Actualizar' : 'Crear Usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ActionConfirmModal
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        confirmLabel={confirmState.confirmLabel}
        confirmClassName={confirmState.confirmClassName}
        onCancel={closeConfirmModal}
        onConfirm={runConfirmAction}
      />

      {credentialModal.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
            <div className="border-b border-slate-100 p-6">
              <h3 className="font-heading text-lg font-bold text-slate-800">Contraseña temporal generada</h3>
              <p className="mt-1 text-sm text-slate-500">
Comparte esta contraseña con <span className="font-semibold text-slate-700">{credentialModal.userName}</span>. En su próximo acceso deberá cambiarla obligatoriamente.
              </p>
            </div>

            <div className="space-y-4 p-6">
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Contraseña temporal</p>
                <div className="mt-2 flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={credentialModal.password}
                    className="w-full rounded-xl border border-amber-200 bg-white px-4 py-3 font-semibold text-slate-800 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => copyToClipboard(credentialModal.password, 'Contraseña temporal copiada')}
                    className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-amber-600"
                  >
                    {copiedValue === credentialModal.password ? <Check size={16} /> : <Copy size={16} />}
                    Copiar
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setCredentialModal({ open: false, userName: '', password: '' })}
                  className="rounded-xl bg-primary px-6 py-2.5 font-semibold text-white transition-colors hover:bg-primary-hover"
                >
                  Entendido
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isPasswordModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 p-6">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-primary/10 p-2 text-primary">
                  <KeyRound size={20} />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-slate-800">Cambiar contraseña</h3>
                  <p className="text-sm text-slate-500">Actualiza la clave del administrador actual.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={closePasswordModal}
                className="text-slate-400 transition-colors hover:text-slate-600"
              >
                <X size={22} />
              </button>
            </div>

            <form onSubmit={handleAdminPasswordChange} className="space-y-5 p-6">
              {renderPasswordField({
                id: 'team-current-password',
                label: 'Contraseña actual',
                field: 'currentPassword',
                visibleKey: 'current',
                placeholder: 'Ingresa tu contraseña actual'
              })}

              {renderPasswordField({
                id: 'team-new-password',
                label: 'Nueva contraseña',
                field: 'newPassword',
                visibleKey: 'next',
                placeholder: 'Define una nueva contraseña'
              })}

              {renderPasswordField({
                id: 'team-confirm-password',
                label: 'Confirmar nueva contraseña',
                field: 'confirmPassword',
                visibleKey: 'confirm',
                placeholder: 'Repite la nueva contraseña'
              })}

              {passwordError && (
                <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  <AlertCircle size={16} />
                  <span>{passwordError}</span>
                </div>
              )}

              <div className="flex justify-end gap-3 border-t border-slate-100 pt-5">
                <button
                  type="button"
                  onClick={closePasswordModal}
                  className="rounded-xl px-5 py-2.5 font-medium text-slate-600 transition-colors hover:bg-slate-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSavingPassword}
                  className="rounded-xl bg-primary px-5 py-2.5 font-medium text-white shadow-lg shadow-primary/20 transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSavingPassword ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Team;
